import { expValEqualsNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import type { GeneralAccessLevel, Principal, PrincipalAndRole } from '../../controlled/commonTypes';
import type { PrincipalsMap } from '../principalsMap';
import { ConfluenceGroupUsageType } from '../queries/__types__/useQueriesShareAndRestrictDialogQuery';
import type {
	ContentRestriction,
	PublishModalGroupInput,
	PublishModalRestrictionsInput,
	PublishModalUserInput,
} from '../queries/publishModalDataToQueriesState';

import type {
	PageGroupRestrictionInput,
	PageUserRestrictionInput,
} from './__types__/UpdateRestrictionsMutation';

type RestrictionType = 'mutation-restrictions' | 'publish-modal-restrictions';

// This is a conditional type that determines the type based on the RestrictionType.
// Depending on the RestrictionType, it decides which specific type to assign.
// For more information, refer to conditional typing
// https://www.typescriptlang.org/docs/handbook/2/conditional-types.html
type UserInput<T extends RestrictionType> = T extends 'mutation-restrictions'
	? PageUserRestrictionInput
	: PublishModalUserInput;

type GroupInput<T extends RestrictionType> = T extends 'mutation-restrictions'
	? PageGroupRestrictionInput
	: PublishModalGroupInput;

type GroupAndUserInput<T extends RestrictionType> = {
	group: GroupInput<T>[];
	user: UserInput<T>[];
};

type RestrictionsInput<T extends RestrictionType> = {
	read: GroupAndUserInput<T>;
	update: GroupAndUserInput<T>;
	includeInvites?: boolean;
};

interface RestrictionsParams {
	stagedGeneralAccessLevel: GeneralAccessLevel;
	principals: PrincipalsMap;
	restrictionType: RestrictionType;
}

export const isUserPendingAccess = (principal: Principal) => {
	if (principal.type === 'user') {
		const warning = principal.knownErrors?.accessWarning;
		return (
			warning === 'pending-site-access-warning' ||
			warning === 'pending-userbase-propagation-warning'
		);
	}

	return false;
};

export function shouldMutationIncludeInvites({
	principals,
	removals,
	principalsPendingAccess,
	removeAll,
}: {
	principals: PrincipalsMap;
	removals: Set<string>;
	principalsPendingAccess: PrincipalAndRole[];
	removeAll: boolean;
}): boolean {
	if (!fg('twcg-444-invite-usd-improvements-m2-gate')) {
		return false;
	}

	if (!expValEqualsNoExposure('twcg-444-invite-usd-improvements-m2', 'cohort', 'variation')) {
		return false;
	}

	if (Array.from(principals.values()).some(({ principal }) => isUserPendingAccess(principal))) {
		return true;
	}

	if (principalsPendingAccess.some(({ principal: { id } }) => removals.has(id))) {
		return true;
	}

	if (removeAll && principalsPendingAccess.length > 0) {
		return true;
	}

	return false;
}

/**
 * Generates mutation restrictions based on the staged general access level and principals map.
 */
export function getRestrictionsForMutation({
	stagedGeneralAccessLevel,
	principals,
}: {
	stagedGeneralAccessLevel: GeneralAccessLevel;
	principals: PrincipalsMap;
}): RestrictionsInput<'mutation-restrictions'> {
	return getRestrictions({
		stagedGeneralAccessLevel,
		principals,
		restrictionType: 'mutation-restrictions',
	});
}

/**
 * Generates publish modal restrictions based on the staged general access level and principals map.
 */
export function getRestrictionsForPublishModal({
	stagedGeneralAccessLevel,
	principals,
}: Pick<
	RestrictionsParams,
	'stagedGeneralAccessLevel' | 'principals'
>): PublishModalRestrictionsInput {
	const currentRestrictions = getRestrictions({
		stagedGeneralAccessLevel,
		principals,
		restrictionType: 'publish-modal-restrictions',
	});

	const read: ContentRestriction = {
		operation: 'read',
		restrictions: { user: { results: [] }, group: { results: [] } },
	};

	const update: ContentRestriction = {
		operation: 'update',
		restrictions: { user: { results: [] }, group: { results: [] } },
	};

	const { publishModalReadRestrictions, publishModalUpdateRestrictions } =
		convertToPublishModalRestrictions(read, update, currentRestrictions);

	return [publishModalReadRestrictions, publishModalUpdateRestrictions];
}

/**
 * Generates restrictions based on the general access level, the provided principals map and restrictionType.
 */
function getRestrictions<T extends RestrictionType>({
	stagedGeneralAccessLevel,
	principals,
	restrictionType,
}: RestrictionsParams): RestrictionsInput<T> {
	switch (stagedGeneralAccessLevel) {
		case 'restricted':
			return {
				read: getReadList(principals, restrictionType),
				update: getUpdateList(principals, restrictionType),
			};
		case 'open-can-view':
			return { read: { group: [], user: [] }, update: getUpdateList(principals, restrictionType) };
		default:
			return { read: { group: [], user: [] }, update: { group: [], user: [] } };
	}
}

/**
 * Converts the current state to publish modal restrictions by transforming user and group lists.
 *
 * @param {ContentRestriction} read - The read restrictions content.
 * @param {ContentRestriction} update - The update restrictions content.
 * @param {PageRestrictionsInput} currentRestrictions - The current restrictions input.
 * @returns {{ publishModalReadRestrictions: ContentRestriction, publishModalUpdateRestrictions: ContentRestriction }} The transformed publish modal restrictions.
 */
function convertToPublishModalRestrictions(
	read: ContentRestriction,
	update: ContentRestriction,
	currentRestrictions: RestrictionsInput<'publish-modal-restrictions'>,
): {
	publishModalReadRestrictions: ContentRestriction;
	publishModalUpdateRestrictions: ContentRestriction;
} {
	const {
		restrictions: {
			user: { results: readUserList },
			group: { results: readGroupList },
		},
	} = read;

	const {
		restrictions: {
			user: { results: updateUserList },
			group: { results: updateGroupList },
		},
	} = update;

	const { read: currentReadList, update: currentUpdateList } = currentRestrictions;

	pushTransformedUser(readUserList, currentReadList.user);
	pushTransformedUser(updateUserList, currentUpdateList.user);
	pushTransformedGroup(readGroupList, currentReadList.group);
	pushTransformedGroup(updateGroupList, currentUpdateList.group);

	return { publishModalReadRestrictions: read, publishModalUpdateRestrictions: update };
}

/**
 * Transforms and adds user restrictions to the publish modal user list.
 */
function pushTransformedUser(list: PublishModalUserInput[], users: PublishModalUserInput[]) {
	for (const user of users) {
		if (!user) continue;
		list.push({
			...user,
			type: 'known', // instead of type: 'user'
			publicName: user.displayName,
			isExternalCollaborator: false,
		});
	}
}

/**
 * Transforms and adds group restrictions to the publish modal group list.
 */
function pushTransformedGroup(list: PublishModalGroupInput[], groups: PublishModalGroupInput[]) {
	for (const group of groups) {
		if (!group) continue;
		list.push(group);
	}
}

const getUsageType = (principal: Principal) => {
	if (
		(principal.type === 'group' || principal.type === 'team') &&
		'usageType' in principal &&
		principal.usageType
	) {
		return principal.usageType;
	}

	if (principal.type === 'group') {
		return ConfluenceGroupUsageType.USERBASE_GROUP;
	}

	if (principal.type === 'team') {
		return ConfluenceGroupUsageType.TEAM_COLLABORATION;
	}
};

/**
 * Generates a list of principals with 'can-edit' access for the update list.
 */
function getUpdateList<T extends RestrictionType>(
	principals: PrincipalsMap,
	restrictionType: T,
): GroupAndUserInput<T> {
	const groupList: GroupInput<T>[] = [];
	const userList: UserInput<T>[] = [];

	for (const [_, value] of principals) {
		const { role, principal } = value;
		const validGroupType = principal.type === 'group' || principal.type === 'team';

		if (validGroupType && role === 'can-edit') {
			groupList.push({
				id: principal.id,
				name: principal.name,
				...(restrictionType === 'publish-modal-restrictions' && { type: 'group' }),
				...(restrictionType === 'publish-modal-restrictions' && {
					usageType: getUsageType(principal),
				}),
			} as GroupInput<T>);
		}

		if (principal.type === 'user' && role === 'can-edit') {
			userList.push({
				...(restrictionType === 'mutation-restrictions' && { id: principal.id }),
				...(restrictionType === 'publish-modal-restrictions' && {
					type: 'user',
					accountId: principal.id,
					displayName: principal.name,
					profilePicture: { path: principal.avatarUrl },
					email: principal.email,
				}),
			} as UserInput<T>);
		}
	}

	return { group: groupList, user: userList };
}

/**
 * Generates a list of principals with 'can-view' access for the read list.
 */
function getReadList<T extends RestrictionType>(
	principals: PrincipalsMap,
	restrictionType: T,
): GroupAndUserInput<T> {
	const groupList: GroupInput<T>[] = [];
	const userList: UserInput<T>[] = [];

	for (const [_, value] of principals) {
		const { role, principal } = value;
		const validGroupType = principal.type === 'group' || principal.type === 'team';

		if (validGroupType && (role === 'can-view' || role === 'can-edit')) {
			groupList.push({
				id: principal.id,
				name: principal.name,
				...(restrictionType === 'publish-modal-restrictions' && { type: 'group' }),
				...(restrictionType === 'publish-modal-restrictions' && {
					usageType: getUsageType(principal),
				}),
			} as GroupInput<T>);
		}

		if (principal.type === 'user' && (role === 'can-view' || role === 'can-edit')) {
			userList.push({
				...(restrictionType === 'mutation-restrictions' && { id: principal.id }),
				...(restrictionType === 'publish-modal-restrictions' && {
					type: 'user',
					accountId: principal.id,
					displayName: principal.name,
					profilePicture: { path: principal.avatarUrl },
					email: principal.email,
				}),
			} as UserInput<T>);
		}
	}

	return { group: groupList, user: userList };
}
